import { createSlice } from '@reduxjs/toolkit';
import i18n from '../../../i18n';

const initialState = {
  units: [],
  unitLevels: [],
  verifyModes: [], // List of verify modes for multi-select
  availableParentUnits: [], // Units available for parent selection
  total: 0,
  page: 1,
  pageSize: 10,
  sortColumn: 'name',
  sortDirection: 'asc',
  filterName: '',
  loading: false,
  error: null,
  successMessage: null,
  selectedUnit: null,
};

const unitsSlice = createSlice({
  name: 'units',
  initialState,
  reducers: {
    // Action to trigger fetch init data (saga will handle this)
    fetchInit: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchInitSuccess: (state, action) => {
      state.loading = false;
      state.unitLevels = action.payload.unitLevels || [];
      state.verifyModes = action.payload.verifyModes || [];
      state.availableParentUnits = action.payload.availableParentUnits || [];
      state.error = null;
    },
    fetchInitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger fetch units (saga will handle this)
    fetchUnits: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchUnitsSuccess: (state, action) => {
      state.loading = false;
      state.units = action.payload.data || [];
      // Extract pagination info from meta object
      state.total = action.payload.meta?.total || 0;
      state.page = action.payload.meta?.page || 1;
      state.pageSize = action.payload.meta?.size || 10;
      state.error = null;
    },
    fetchUnitsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger add unit
    addUnit: (state) => {
      state.loading = true;
      state.error = null;
    },
    addUnitSuccess: (state, action) => {
      state.loading = false;
      state.units.push(action.payload);
      state.error = null;
      state.successMessage = i18n.t('units.success.created');
    },
    addUnitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger fetch single unit
    fetchUnitById: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchUnitByIdSuccess: (state, action) => {
      state.loading = false;
      state.selectedUnit = action.payload;
      state.error = null;
    },
    fetchUnitByIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger update unit
    updateUnit: (state) => {
      state.loading = true;
      state.error = null;
    },
    updateUnitSuccess: (state, action) => {
      state.loading = false;
      const index = state.units.findIndex((unit) => unit.id === action.payload.id);
      if (index !== -1) {
        state.units[index] = action.payload;
      }
      state.error = null;
      state.successMessage = i18n.t('units.success.updated');
    },
    updateUnitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger delete unit
    deleteUnit: (state) => {
      state.loading = true;
      state.error = null;
    },
    deleteUnitSuccess: (state, action) => {
      state.loading = false;
      state.units = state.units.filter((unit) => unit.id !== action.payload);
      state.error = null;
      state.successMessage = i18n.t('units.success.deleted');
    },
    deleteUnitFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Action to trigger bulk delete units
    bulkDeleteUnits: (state) => {
      state.loading = true;
      state.error = null;
    },
    bulkDeleteUnitsSuccess: (state, action) => {
      state.loading = false;
      state.units = state.units.filter((unit) => !action.payload.includes(unit.id));
      state.error = null;
      state.successMessage = i18n.t('units.success.bulkDeleted', { count: action.payload.length });
    },
    bulkDeleteUnitsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },
    // Set selected unit
    setSelectedUnit: (state, action) => {
      state.selectedUnit = action.payload;
    },
    // Clear error
    clearError: (state) => {
      state.error = null;
    },
    // Clear success message
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    // Clear selected unit
    clearSelectedUnit: (state) => {
      state.selectedUnit = null;
    },
    // Set pagination
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1; // Reset to first page when page size changes
    },
    // Set sort
    setSortColumn: (state, action) => {
      state.sortColumn = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },
    // Set filter
    setFilterName: (state, action) => {
      state.filterName = action.payload;
      state.page = 1; // Reset to first page when filter changes
    },
  },
});

// Export actions
export const {
  fetchInit,
  fetchInitSuccess,
  fetchInitFailure,
  fetchUnits,
  fetchUnitsSuccess,
  fetchUnitsFailure,
  fetchUnitById,
  fetchUnitByIdSuccess,
  fetchUnitByIdFailure,
  addUnit,
  addUnitSuccess,
  addUnitFailure,
  updateUnit,
  updateUnitSuccess,
  updateUnitFailure,
  deleteUnit,
  deleteUnitSuccess,
  deleteUnitFailure,
  bulkDeleteUnits,
  bulkDeleteUnitsSuccess,
  bulkDeleteUnitsFailure,
  setSelectedUnit,
  clearError,
  clearSuccessMessage,
  clearSelectedUnit,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterName,
} = unitsSlice.actions;

// Selectors
export const selectUnits = (state) => state.units.units;
export const selectUnitLevels = (state) => state.units.unitLevels;
export const selectVerifyModes = (state) => state.units.verifyModes;
export const selectAvailableParentUnits = (state) => state.units.availableParentUnits;
export const selectUnitsLoading = (state) => state.units.loading;
export const selectUnitsError = (state) => state.units.error;
export const selectUnitsSuccessMessage = (state) => state.units.successMessage;
export const selectSelectedUnit = (state) => state.units.selectedUnit;
export const selectUnitsTotal = (state) => state.units.total;
export const selectUnitsPage = (state) => state.units.page;
export const selectUnitsPageSize = (state) => state.units.pageSize;
export const selectUnitsSortColumn = (state) => state.units.sortColumn;
export const selectUnitsSortDirection = (state) => state.units.sortDirection;
export const selectUnitsFilterName = (state) => state.units.filterName;

// Export reducer
export default unitsSlice.reducer;
